import React, { useMemo, useRef, useState, useEffect, useImperativeHandle } from 'react'
import type { Editor as TiptapEditor } from '@tiptap/react'
import { HtmlContent, PopupMenuItem, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { getPresentationContentStylesNext } from '@paintscout/ui/styles'
import TextFieldsOutlinedIcon from '@material-ui/icons/TextFieldsOutlined'
import type { QuoteDocument } from 'paintscout'
import { HtmlEditor, Placeholder, PresEditorTooltip } from '../../components'
import { getPresentationTemplateContext } from '../../util'
import { keepsTextFocusOnBlur, TEXT_ELEMENT_KEY_PREFIX } from '../../util/presentationEditorDom'
import { useDebouncedCommit } from '../../util/useDebouncedCommit'
import {
  buildPlaceholderLabelMap,
  templateFallbackStyles,
  templatePlaceholderLineHeight,
  templatePlaceholderStyles,
  templateToPlaceholderHtml,
  useClientOptions
} from '@paintscout/ui-business'
import { getTemplateTags } from '@paintscout/util/util'
import { template } from '@paintscout/util/templater'
import SyncIcon from '@material-ui/icons/Sync'
import classnames from 'classnames'
import { usePresentation, useQuote } from '../../../context'
import { usePresentationNav } from '../../PresentationNav/PresentationNavContext'

const FALLBACK_CLASS = 'template-fallback'

/** In a preview, marks a fallback that stood in for a missing value so the author can spot it. */
const highlightFallback = (fallback: string) => `<span class="${FALLBACK_CLASS}">${fallback}</span>`

export interface SmartContentOption {
  key: string
  label: string
  resolve: (quote: QuoteDocument) => string | null
}

interface InlineEditableHtmlProps {
  content: string
  placeholderButtonText?: string
  handleEdit: (content: string) => void
  classes?: StyleClasses<typeof useStyles>
  isEditable?: boolean
  isSettings?: boolean
  header?: boolean
  presetColors?: string[]
  /**
   * This field's identity within the block (e.g. `<blockPrefix>.fields.content`) — used as the
   * inspector's `focusedElement` key (`text::<fieldKey>`) when this field's TipTap editor has caret
   * focus. Required to disambiguate which field is "live" when a block has more than one
   * (#22203) — omit only for fields that never need an inspector toolbar.
   */
  fieldKey?: string
  /** Available "smart content" sources (e.g. "quote/job address") this field can bind to. */
  smartContentOptions?: SmartContentOption[]
  /** Currently-selected smart content key (null/undefined = manual). */
  smartContentKey?: string | null
  /** Persists the smart content selection (a sibling field, alongside the manual content). */
  onSmartContentChange?: (key: string | null) => void
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      position: 'relative',
      '& .ProseMirror p': {
        '&:after': {
          display: 'none'
        }
      },
      color: theme.palette.text.primary,
      // Chips rendered read-only (unfocused fields, while the editor is open) get the same look as
      // the ones inside the RTE — the styles live in one place so they cannot drift.
      '& p:has(.template-placeholder)': templatePlaceholderLineHeight,
      '& .template-placeholder': templatePlaceholderStyles(theme),
      [`& .${FALLBACK_CLASS}`]: templateFallbackStyles
    },
    editorRoot: {
      cursor: 'text',
      minHeight: '0 !important',
      border: 'none !important',
      color: 'inherit',
      overflow: 'hidden'
    },
    placeholder: {},
    editor: {
      '& div': {
        padding: '0 !important',
        '& p': {
          margin: '0 !important'
        }
      }
    },
    smartContentRow: {
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.borderRadius.md,
      boxShadow: theme.boxShadow[1],
      display: 'flex',
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2),
      zIndex: 3
    }
  }
})

export type InlineEditableHtmlRef = {
  getContent: () => string
}

const InlineEditableHtml = React.forwardRef(function InlineEditableHtml(
  props: InlineEditableHtmlProps,
  ref: React.Ref<InlineEditableHtmlRef>
) {
  const classes = useStyles(props)
  const {
    handleEdit,
    isSettings,
    isEditable,
    placeholderButtonText,
    header,
    presetColors,
    smartContentOptions,
    smartContentKey,
    onSmartContentChange,
    fieldKey
  } = props
  const { quote } = useQuote()
  const { options } = useClientOptions()
  // Set only by the presentation editor page (PresentationEdit) — the signal for "we are editing
  // this presentation", as opposed to `isEditable` below which means "this field has focus".
  const { isEditable: isEditingPresentation, focusedElement, setFocusedElement, presentationTheme } = usePresentation()
  // The focused TipTap editor styles itself against the ambient app theme by default, not this
  // presentation's theme — passing the same styles the read-only render uses keeps a field's font
  // and color from reverting to the app default the moment it's clicked into (ENG-1438).
  const contentStyles = useMemo(
    () => (presentationTheme ? getPresentationContentStylesNext(presentationTheme, { noMargins: ['li'] }) : undefined),
    [presentationTheme]
  )
  const [editing, setEditing] = useState(false)
  const [content, setContent] = useState(props.content)

  // The debounced commit (below) round-trips whatever we just typed back down through Formik into
  // `props.content` a moment later — this ref lets the sync effect tell that echo apart from a real
  // external change (undo, this instance getting reused for a different block on reorder, a smart
  // content source reverting). Only a value we did NOT ourselves just commit should reset `content`.
  // This relies on the round-tripped string coming back byte-identical to what we wrote — Formik
  // doesn't touch it, so that holds today. It would break if something started re-serialising the
  // HTML in between (a sanitiser, whitespace/entity normalisation on save, a GraphQL round trip
  // that doesn't preserve the string exactly) — the symptom would be this bug's exact return: the
  // field blurring again ~400ms into typing, since the guard below would stop matching and fall
  // back to resetting `content` on every commit.
  const lastCommittedRef = useRef(props.content)

  // reset contents to match props, needed for undo — but not for the echo of our own commit above.
  useEffect(() => {
    if (props.content === lastCommittedRef.current) return
    setContent(props.content)
  }, [props.content])

  // The presentation-wide set of `{{key}}` tags (client name, company name, job address, etc.)
  // that can be inserted inline into free-typed text and resolved to real values on read.
  const templateTags = useMemo(
    () => getTemplateTags({ options, invoice: quote?.is_invoice }),
    [options, quote?.is_invoice]
  )
  const templateContext = useMemo(() => getPresentationTemplateContext({ quote, options }), [quote, options])

  // A smart content source binds this field to a live, quote-derived value (e.g. the quote/job
  // address) instead of a fixed one — resolved fresh from whichever quote is in context, so the
  // same template shows the right text per quote. The manually-typed `content` is preserved
  // independently underneath so switching the source off reverts to it. While actively editing,
  // the manual content is always what's shown/edited — smart content only takes over the
  // read/display state.
  const activeSmartContent = smartContentOptions?.find((option) => option.key === smartContentKey)
  // `isPreview` is set by every in-app preview (PresentationImbedPreview) and never by the customer
  // view or the PDF, which show a fallback as plain text.
  const { isPreview } = usePresentationNav() ?? {}
  const resolvedManualContent = useMemo(
    () =>
      content
        ? template({
            text: content,
            context: templateContext,
            renderFallback: isPreview ? highlightFallback : undefined
          })
        : content,
    [content, templateContext, isPreview]
  )
  const resolvedContent = activeSmartContent
    ? activeSmartContent.resolve(quote) ?? resolvedManualContent
    : resolvedManualContent
  const isSmartActive = !!activeSmartContent && !editing

  // While the presentation editor is open, an unfocused field shows its `{{key}}` tags as chips
  // rather than resolving them. Two reasons: tags stay visible across the whole page instead of
  // only in whichever field has focus, and there is no real quote behind the editor — the context
  // resolves to empty strings, so resolving here made tags vanish the moment a field lost focus.
  // This is a plain string → HTML transform rendered by HtmlContent; no editor is mounted for it.
  const showTagsUnresolved = isEditingPresentation && !activeSmartContent
  const placeholderContent = useMemo(
    () => (content ? templateToPlaceholderHtml(content, buildPlaceholderLabelMap(templateTags)) : content),
    [content, templateTags]
  )
  const displayContent = showTagsUnresolved ? placeholderContent : resolvedContent

  const nullishContent = !content || content === '<p></p>'
  // Emptiness has to be judged on what we actually render: a field holding only `{{clientName}}`
  // resolves to '' but is not empty when shown as a chip.
  const nullishDisplayContent = !displayContent || displayContent === '<p></p>'

  useImperativeHandle(ref, () => ({
    getContent: () => {
      return content || props.content
    }
  }))

  // instead of listening for a blur on the editor, we listen for a click outside of the block
  useEffect(() => {
    if (editing) {
      handleConfirm()
    }
  }, [isEditable])

  // Both commit paths go through this so `lastCommittedRef` always reflects the latest value we
  // ourselves wrote, whether it landed via the debounce or via blur/click-away.
  const commit = (value: string) => {
    lastCommittedRef.current = value
    handleEdit(value)
  }

  // Typing commits on a debounce as well, so the form is dirty (and Save is clickable) while the
  // user is still in the block — see useDebouncedCommit for why. The click-away commit below stays:
  // it is what guarantees the final value lands, whatever the timer was doing.
  const draftCommit = useDebouncedCommit(commit)

  const handleConfirm = (ev?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (ev) ev.stopPropagation()
    // Drop any scheduled commit first — it holds the same content this is about to write, and
    // letting it fire afterwards would re-write it into a block that may have moved on.
    draftCommit.cancel()
    commit(content)
    setEditing(false)
  }

  const handleChange = (content: string) => {
    setContent(content)
    draftCommit.schedule(content)
  }

  // Only set while the presentation editor itself is open — never in read-only/customer-view
  // rendering, which doesn't mount HtmlEditor as an editable field anyway (isEditable above already
  // gates that). The blur guard only clears the slot if it's still this field's — TipTap fires
  // blur-then-focus in that order on a fast hop to a different text field in the same block, and a
  // stale blur would otherwise clobber the field that just took focus.
  const textElementKey = fieldKey ? `${TEXT_ELEMENT_KEY_PREFIX}${fieldKey}` : undefined

  // TipTap's Editor binds onFocus/onBlur once, in its constructor, and never re-subscribes them —
  // `useEditor`'s per-render `setOptions()` call updates `editor.options.onFocus` but nothing re-runs
  // the `this.on('focus', ...)` registration, so the specific function passed on the *first* render
  // is what fires for the life of the editor instance. `handleEditorFocus`/`handleEditorBlur` below
  // are recreated every render, but only the first ones TipTap ever saw actually run — so anything
  // they read from render-scoped variables (as opposed to a prop/ref) would be permanently stuck at
  // its value from that first render. `latestRef` gives that permanently-bound closure a way to see
  // current values anyway.
  const latestRef = useRef({ focusedElement, templateTags, templateContext })
  latestRef.current = { focusedElement, templateTags, templateContext }

  const handleEditorFocus = isEditingPresentation
    ? (editor: TiptapEditor) => {
        if (!textElementKey) return
        const { templateTags, templateContext } = latestRef.current
        setFocusedElement({ type: 'text', key: textElementKey, editor, templateTags, templateContext })
      }
    : undefined
  const handleEditorBlur = isEditingPresentation
    ? (_editor: TiptapEditor, event?: FocusEvent) => {
        if (!textElementKey) return
        // Focus moving into the inspector is not the user leaving this field — it is the user
        // reaching for a control that edits it, and the inspector's link-URL input is the one such
        // control that must take real DOM focus. Clearing the slot here unmounts the toolbar that
        // input lives in, which made adding a link impossible (#22887).
        if (keepsTextFocusOnBlur(event)) return
        const { focusedElement } = latestRef.current
        if (focusedElement?.type === 'text' && focusedElement.key === textElementKey) {
          setFocusedElement(null)
        }
      }
    : undefined

  // Template tags are inserted from the inspector's text toolbar (see EditorPlaceholderPicker),
  // so they don't live in this corner row.
  const editorToolsRow = smartContentOptions?.length > 0 && !isSmartActive && (
    <div className={classnames(classes.smartContentRow)} onClick={(ev) => ev.stopPropagation()}>
      {smartContentOptions.map((option) => (
        <PresEditorTooltip key={option.key} title={option.label}>
          <PopupMenuItem
            icon={SyncIcon}
            iconOnly
            selected={smartContentKey === option.key}
            onClick={() => onSmartContentChange?.(smartContentKey === option.key ? null : option.key)}
          />
        </PresEditorTooltip>
      ))}
    </div>
  )

  return (
    <>
      {isEditable && (
        <div
          onClick={() => {
            if (!editing) setEditing(true)
          }}
          className={classes.root}
        >
          {editorToolsRow}
          {isSmartActive && <HtmlContent content={resolvedContent ?? ''} />}
          {!isSmartActive && (!nullishContent || editing) && (
            // wrapping the RTE in a div (somehow) prevents errors from improper FloatingMenu cleanup on RTE unmount
            <div>
              <HtmlEditor
                tippyOptions={{ appendTo: document.querySelector('#BasePage-root'), zIndex: 4 }}
                header={header}
                value={content}
                classes={{ root: classes.editorRoot, editor: classes.editor }}
                onChange={handleChange}
                autofocus={nullishContent ? 'start' : null}
                hasColor={true}
                presetColors={presetColors}
                templateTags={templateTags}
                disableToolbar
                onEditorFocus={handleEditorFocus}
                onEditorBlur={handleEditorBlur}
                contentStyles={contentStyles}
              />
            </div>
          )}
          {!isSmartActive && nullishContent && !editing && (
            <Placeholder
              classes={{ placeholder: classes.placeholder }}
              Icon={TextFieldsOutlinedIcon}
              buttonText={placeholderButtonText}
            />
          )}
        </div>
      )}
      {!isEditable && (
        <>
          {!nullishDisplayContent && <HtmlContent className={classes.root} content={displayContent} />}
          {nullishDisplayContent && isSettings && (
            <Placeholder
              classes={{ placeholder: classes.placeholder }}
              Icon={TextFieldsOutlinedIcon}
              buttonText={placeholderButtonText}
            />
          )}
        </>
      )}
    </>
  )
})

export default InlineEditableHtml
